'use client';

import React from 'react';
import type { RefAttributes, TouchEventHandler } from 'react';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import { Popover as AriaPopover } from 'react-aria-components';
import { useSurface } from '@milanote/roma/components/base/surface/surface';
import { cx } from '@milanote/roma/utils/cx';
import { styles, type SelectSize } from './select-field';

const SEMANTIC_NAME = 'RomaPopover';

interface PopoverProps extends AriaPopoverProps, RefAttributes<HTMLElement> {
    size: SelectSize;
}

export const Popover = (props: PopoverProps) => {
    const surface = useSurface();
    const { className, onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, size, ...rest } = props;

    const stopTouchPropagation = (handler?: TouchEventHandler<HTMLElement>): TouchEventHandler<HTMLElement> => {
        return (event) => {
            handler?.(event);
            event.stopPropagation();
        };
    };

    return (
        <AriaPopover
            placement="bottom"
            containerPadding={0}
            offset={4}
            {...rest}
            data-sheet-ignore-drags
            onTouchStart={stopTouchPropagation(onTouchStart)}
            onTouchMove={stopTouchPropagation(onTouchMove)}
            onTouchEnd={stopTouchPropagation(onTouchEnd)}
            onTouchCancel={stopTouchPropagation(onTouchCancel)}
            className={(state) =>
                cx(
                    SEMANTIC_NAME,
                    surface,
                    'w-(--trigger-width) origin-(--trigger-anchor-point) overflow-x-hidden overflow-y-auto rounded-md bg-primary py-1.25 shadow-lg outline-hidden will-change-transform',
                    styles.sizes[size].popover,
                    state.isEntering &&
                        'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
                    state.isExiting &&
                        'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5',
                    typeof className === 'function' ? className(state) : className,
                )
            }
        />
    );
};

Popover.displayName = SEMANTIC_NAME;
